import React from 'react';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Row,
    Col,
    Card,
    CardBody,
    Badge,
} from 'reactstrap';

const TransactionDetailDialog = ({ open, onClose, transaction, user }) => {
    const getStatusVariant = (status) => {
        const variants = {
            paid: 'success',
            draft: 'secondary',
            unpaid: 'warning',
            failed: 'danger',
            in_progress: 'info',
            void: 'secondary',
            active: 'success',
            canceled: 'danger',
            cancelled: 'danger',
            past_due: 'warning',
            pad: 'warning'
        };
        return variants[status] || 'secondary';
    };

    const getStatusLabel = (status) => {
        const labels = {
            paid: 'PAID',
            draft: 'DRAFT',
            unpaid: 'UNPAID',
            failed: 'FAILED',
            in_progress: 'IN PROGRESS',
            void: 'VOID',
            active: 'ACTIVE',
            canceled: 'CANCELED',
            cancelled: 'CANCELLED',
            past_due: 'PAST DUE',
            pad: 'PAD'
        };
        return labels[status] || status?.toUpperCase() || '-';
    };

    const getStatusBadgeClass = (status) => {
        if (!status) return "badge-soft-secondary";
        const normalized = status.toLowerCase();
        if (normalized === 'active' || normalized === 'paid') return "badge-soft-success";
        if (normalized === 'canceled' || normalized === 'cancelled' || normalized === 'unpaid' || normalized === 'failed')
            return "badge-soft-danger";
        if (normalized === 'past_due' || normalized === 'in_progress' || normalized === 'pad') return "badge-soft-warning";
        if (normalized === 'draft' || normalized === 'void') return "badge-soft-secondary";
        return "badge-soft-secondary";
    };

    const formatCurrency = (amount, currency = 'USD') => {
        if (amount === undefined || amount === null || amount === "") {
            return "-";
        }
        const normalizedCurrency = currency.toUpperCase();
        return `${(amount / 100).toFixed(2)} ${normalizedCurrency}`;
    };

    const formatDateTime = (timestamp) => {
        if (!timestamp) return "-";
        try {
            const date = new Date(timestamp * 1000);
            return date.toLocaleString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
            });
        } catch (error) {
            return "-";
        }
    };

    const status = transaction.detailedStatus || transaction.status;
    const statusBadgeClass = getStatusBadgeClass(status);

    return (
        <Modal
            isOpen={open}
            toggle={onClose}
            size="lg"
            className="modal-dialog-scrollable subscription-modal"
        >
            <ModalHeader toggle={onClose} className="modal-title text-body fw-semibold">
                Transaction Details
            </ModalHeader>

            <ModalBody>
                {transaction ? (
                    <div className="transaction-details">
                        {/* Header Section */}
                        <Card className="border-0 shadow-sm mb-4">
                            <CardBody>
                                <Row className="align-items-start">
                                    <Col md={6}>
                                        <div className="d-flex align-items-start gap-3">
                                            <div className="flex-shrink-0">
                                                <div 
                                                    className="bg-light rounded-circle d-flex align-items-center justify-content-center"
                                                    style={{ width: '50px', height: '50px' }}
                                                >
                                                    <i className="ri-user-line text-muted fs-5"></i>
                                                </div>
                                            </div>
                                            <div className="flex-grow-1">
                                                <p className="text-muted mb-1 small fw-semibold">User</p>
                                                <h5 className="mb-1 text-body">
                                                    {user?.firstname} {user?.lastname || '-'}
                                                </h5>
                                                <p className="mb-1">
                                                    {user?.email ? (
                                                        <a href={`mailto:${user.email}`} className="text-decoration-none">
                                                            {user.email}
                                                        </a>
                                                    ) : (
                                                        "-"
                                                    )}
                                                </p>
                                                <p className="mb-0 text-muted small">
                                                    User ID: <code className="text-muted">{user?._id || '-'}</code>
                                                </p>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="d-flex justify-content-between align-items-start h-100">
                                            <div className="text-start">
                                                <p className="text-muted mb-1 small fw-semibold">Status</p>
                                                <span className={`badge ${statusBadgeClass} fs-6`}>
                                                    {getStatusLabel(status)}
                                                </span>
                                            </div>
                                            <div className="text-end">
                                                <p className="text-muted mb-1 small fw-semibold">Amount</p>
                                                <h4 className="mb-1 text-body">
                                                    {formatCurrency(transaction.amount_paid || transaction.amount_due, transaction.currency)}
                                                </h4>
                                                <small className="text-muted">
                                                    {transaction.description || 'Subscription Payment'}
                                                </small>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>

                        <Row className="g-3">
                            {/* Left Column - Transaction & Amount Information */}
                            <Col md={6}>
                                <div className="d-flex flex-column h-100">
                                    {/* Transaction Information */}
                                    <Card className="border shadow-sm flex-fill" style={{ minHeight: '250px' }}>
                                        <CardBody className="d-flex flex-column">
                                            <h6 className="text-muted text-uppercase mb-3 fw-semibold small">
                                                Transaction Information
                                            </h6>
                                            <div className="d-flex flex-column gap-3 flex-fill">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <span className="text-muted small">Invoice ID</span>
                                                    <span className="fw-semibold text-body text-end">
                                                        {transaction.number || '-'}
                                                    </span>
                                                </div>
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <span className="text-muted small">Transaction ID</span>
                                                    <span className="fw-semibold text-body text-end">
                                                        <code className="text-primary small">{transaction.id}</code>
                                                    </span>
                                                </div>
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <span className="text-muted small">Date</span>
                                                    <span className="fw-semibold text-body text-end small">
                                                        {formatDateTime(transaction.created)}
                                                    </span>
                                                </div>
                                                {transaction.payment_intent && (
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <span className="text-muted small">Payment Intent</span>
                                                        <span className="fw-semibold text-body text-end">
                                                            <code className="text-primary small">{transaction.payment_intent}</code>
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </CardBody>
                                    </Card>

                                    {/* Amount Breakdown */}
                                    <Card className="border shadow-sm flex-fill" style={{ minHeight: '250px' }}>
                                        <CardBody className="d-flex flex-column">
                                            <h6 className="text-muted text-uppercase mb-3 fw-semibold small">
                                                Amount Breakdown
                                            </h6>
                                            <div className="d-flex flex-column gap-3 flex-fill">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <span className="text-muted small">Amount Due</span>
                                                    <span className="fw-semibold text-body text-end">
                                                        {formatCurrency(transaction.amount_due, transaction.currency)}
                                                    </span>
                                                </div>
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <span className="text-muted small">Amount Paid</span>
                                                    <span className={`fw-semibold text-end ${transaction.amount_paid > 0 ? 'text-success' : 'text-muted'}`}>
                                                        {formatCurrency(transaction.amount_paid, transaction.currency)}
                                                    </span>
                                                </div>
                                                <div className="mt-auto pt-3 border-top">
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <strong className="small">Remaining</strong>
                                                        <strong className={`text-end ${transaction.amount_remaining > 0 ? 'text-warning' : 'text-success'}`}>
                                                            {formatCurrency(transaction.amount_remaining, transaction.currency)}
                                                        </strong>
                                                    </div>
                                                </div>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </div>
                            </Col>

                            {/* Right Column - Subscription & Payment Information */}
                            <Col md={6}>
                                <div className="d-flex flex-column h-100">
                                    {/* Subscription Information */}
                                    {transaction.subscription && (
                                        <Card className="border shadow-sm flex-fill" style={{ minHeight: '250px' }}>
                                            <CardBody className="d-flex flex-column">
                                                <h6 className="text-muted text-uppercase mb-3 fw-semibold small">
                                                    Subscription Details
                                                </h6>
                                                <div className="d-flex flex-column gap-3 flex-fill">
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <span className="text-muted small">Plan</span>
                                                        <span className="fw-semibold text-body text-end">
                                                            <Badge color="primary" className="ms-1">
                                                                {transaction.subscription.planType || '-'}
                                                            </Badge>
                                                        </span>
                                                    </div>
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <span className="text-muted small">Status</span>
                                                        <span className="fw-semibold text-body text-end">
                                                            <span className={`badge ${getStatusBadgeClass(transaction.subscription.status)}`}>
                                                                {getStatusLabel(transaction.subscription.status)}
                                                            </span>
                                                        </span>
                                                    </div>
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <span className="text-muted small">Period End</span>
                                                        <span className="fw-semibold text-body text-end small">
                                                            {transaction.subscription.currentPeriodEnd ?
                                                                formatDateTime(transaction.subscription.currentPeriodEnd) : '-'
                                                            }
                                                        </span>
                                                    </div>
                                                    {transaction.subscription.interval && (
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <span className="text-muted small">Billing Cycle</span>
                                                            <span className="fw-semibold text-body text-end small">
                                                                {transaction.subscription.interval_count || 1} {transaction.subscription.interval}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </CardBody>
                                        </Card>
                                    )}

                                    {/* Payment Information */}
                                    <Card className="border shadow-sm flex-fill" style={{ minHeight: '250px' }}>
                                        <CardBody className="d-flex flex-column">
                                            <h6 className="text-muted text-uppercase mb-3 fw-semibold small">
                                                Payment Information
                                            </h6>
                                            <div className="d-flex flex-column gap-3 flex-fill">
                                                {transaction.payment_method_type && (
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <span className="text-muted small">Payment Method</span>
                                                        <span className="fw-semibold text-body text-end text-uppercase small">
                                                            {transaction.payment_method_type}
                                                        </span>
                                                    </div>
                                                )}
                                                {transaction.payment_intent && (
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <span className="text-muted small">Payment Intent</span>
                                                        <span className="fw-semibold text-body text-end">
                                                            <code className="text-primary small">{transaction.payment_intent}</code>
                                                        </span>
                                                    </div>
                                                )}
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <span className="text-muted small">Currency</span>
                                                    <span className="fw-semibold text-body text-end text-uppercase small">
                                                        {transaction.currency || 'USD'}
                                                    </span>
                                                </div>
                                                
                                                {/* Action Buttons */}
                                                <div className="mt-auto pt-3 border-top">
                                                    <div className="d-flex gap-2 flex-wrap">
                                                        {transaction.receipt_url && (
                                                            <Button
                                                                size="sm"
                                                                color="primary"
                                                                outline
                                                                className="flex-fill"
                                                                onClick={() => window.open(transaction.receipt_url, '_blank')}
                                                            >
                                                                <i className="ri-external-link-line me-1"></i>
                                                                View Receipt
                                                            </Button>
                                                        )}
                                                        {transaction.invoice_pdf && (
                                                            <Button
                                                                size="sm"
                                                                color="primary"
                                                                className="flex-fill"
                                                                onClick={() => window.open(transaction.invoice_pdf, '_blank')}
                                                            >
                                                                <i className="ri-download-line me-1"></i>
                                                                Download Invoice
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </div>
                            </Col>
                        </Row>
                    </div>
                ) : (
                    <div className="text-center py-4">
                        <p>Loading transaction details...</p>
                    </div>
                )}
            </ModalBody>

            <ModalFooter>
                <Button color="secondary" onClick={onClose}>
                    Close
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default TransactionDetailDialog;